import {
  Text,
  Container,
  Graphics,
} from 'pixi.js';
import gsap from 'gsap';
import { max } from 'lodash';
import WebGLElement from './WebGLElement';
import overlay from '.';
import { isMobile } from '../utils';

class WebGLTitle extends WebGLElement {
  constructor(component, element) {
    super(component, element);

    this.element = element;
    this.sprite = new Container();
    this.style = {};
    this.lines = [];

    if (this.component.hasDisclaimer) {
      this.priceExclusions = new Text('Exclusions Apply*', {
        fontFamily: 'VHS',
        fontSize: 12,
        fill: 0xffffff,
      });
    }

    overlay.ref.push(this);

    window.addEventListener('webgl-loaded', () => {
      this.sprite.filters = [overlay.ref.displace];
    });
  }

  init() {
    this.setProps();
    this.createLines();
    this.initLines();

    if (this.component.hasDisclaimer) {
      this.setPrice();
      this.sprite.addChild(this.priceExclusions);
    }

    this.element.style.visibility = 'hidden';

    this.render(true);
    this.renderHandler = () => { this.render(); };
    window.addEventListener('render', this.renderHandler);
  }

  createLines() {
    this.linesElements = [...this.element.querySelectorAll('.title__line')];
    this.linesElements.forEach((line) => {
      const lineSprite = new Text(line.innerText, this.style);
      const lineObject = {
        element: line,
        sprite: lineSprite,
        mask: null,
        letters: [],
      };
      this.lines.push(lineObject);

      const lettersElements = [...line.querySelectorAll('.title__letter')];
      this.hasLetters = lettersElements.length > 0;
      lettersElements.forEach((letter) => {
        const letterSprite = new Text(letter.innerText, this.style);
        const letterObject = {
          element: letter,
          sprite: letterSprite,
          mask: null,
        };
        lineObject.letters.push(letterObject);
      });
    });
  }

  initLines() {
    this.lines.forEach((line, index) => {
      this.sprite.addChild(line.sprite);
      line.sprite.position.y = index * line.sprite.style.lineHeight;
      if (line.letters.length > 0) line.sprite.style.fill = 'transparent';
      if (this.component.orientation === 'center' || this.component.orientation === 'huge') {
        line.sprite.position.x += ((this.getMaxWidth() - line.sprite.width) / 2);
      }
      if (this.component.orientation === 'right') {
        line.sprite.position.x += this.sprite.width - line.sprite.width;
      }

      const lineMask = new Graphics();
      lineMask.beginFill(0xffffff)
        .drawRect(line.sprite.position.x, line.sprite.position.y, line.sprite.width, line.sprite.height)
        .endFill();
      this.sprite.addChild(lineMask);
      line.mask = lineMask;
      line.sprite.mask = line.mask;

      line.letters.forEach((letter, i) => {
        line.sprite.addChild(letter.sprite);
        if (i > 0) {
          letter.sprite.position.x += line.letters[i - 1].sprite.width + line.letters[i - 1].sprite.position.x - (isMobile() ? 2 : 5);
        }
      });
    });
  }

  setProps() {
    const style = {};
    style.wordWrapWidth = this.element.getBoundingClientRect().width;
    style.fontSize = parseFloat(window.getComputedStyle(this.element).fontSize);
    style.lineHeight = parseFloat(window.getComputedStyle(this.element).lineHeight);
    style.fontFamily = window.getComputedStyle(this.element).fontFamily;
    style.fontWeight = window.getComputedStyle(this.element).fontWeight;
    style.fill = window.getComputedStyle(this.element).color;
    style.align = window.getComputedStyle(this.element).textAlign;
    style.textBaseLine = 'bottom';
    this.style = style;
  }

  render(first = false) {
    if (this.component.$el.dataset.inViewport !== '' && !first) {
      this.sprite.visible = false;
      this.sprite.renderable = false;
      return;
    }
    this.sprite.visible = true;
    this.sprite.renderable = true;
    const rect = this.element.getBoundingClientRect();
    this.sprite.position.x = rect.left;
    this.sprite.position.y = rect.top;
  }

  resize() {
    this.destroyText();
    this.element.style.visibility = 'visible';
    this.setProps();
    this.createLines();
    this.initLines();
    if (this.component.hasDisclaimer) this.setPrice();
    this.render(true);
    this.element.style.visibility = 'hidden';
  }

  initial() {
    if (this.hasLetters) {
      this.lines.forEach((line) => {
        line.letters.forEach((letter) => {
          letter.sprite.originalPosition = letter.sprite.position.y;
          letter.sprite.position.y += line.sprite.style.lineHeight;
        });
      });
    } else {
      this.lines.forEach((line) => {
        line.sprite.originalPosition = line.sprite.position.y;
        line.sprite.position.y += line.sprite.style.lineHeight;
      });
    }
  }

  in() {
    if (this.hasLetters) {
      const tl = gsap.timeline();
      tl.addLabel('start', 1.6);
      this.lines.forEach((line, index) => {
        const lineTl = gsap.timeline();
        lineTl.addLabel('start');
        line.letters.forEach((letter, i) => {
          lineTl.to(letter.sprite.position, {
            y: letter.sprite.originalPosition,
            duration: 1,
            ease: 'power3.out',
          }, `start+=${0.08 * i}`);
        });
        tl.add(lineTl, `start+=${0.2 * index}`);
      });
    } else {
      this.lines.forEach((line, index) => {
        gsap.to(line.sprite.position, {
          y: line.sprite.originalPosition,
          duration: 1,
          ease: 'power3.out',
          delay: 0.3 + (0.2 * index),
        });
      });
    }
  }

  setPrice() {
    this.priceExclusions.position.x = 0;
    this.priceExclusions.position.y = this.sprite.height;
  }

  getMaxWidth() {
    const widths = [];
    this.lines.forEach(line => widths.push(line.sprite.width));
    return max(widths);
  }

  destroyText() {
    this.lines.forEach((line) => {
      line.sprite.destroy();
      // line.mask.destroy();
      line.letters.forEach((letter) => {
        letter.sprite.destroy();
      });
    });
    this.lines = [];
  }

  destroy() {
    window.removeEventListener('render', this.renderHandler);
    this.destroyText();
    this.sprite.destroy({ children: true });
  }
}

export default WebGLTitle;
